import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, ChevronLeft, ChevronRight, Check, Loader2, Plug, Package, Globe, Building2 } from 'lucide-react';
import axios from 'axios';
import config from '../../../config';
import { buildAccountName } from '../../../utils/accountNaming';

// Logos
import googleAdsLogo from '../../../logos/google-ads.png';
import microsoftAdsLogo from '../../../logos/microsoft-ads.png';
import metaAdsLogo from '../../../logos/meta-ads.png';
import pinterestAdsLogo from '../../../logos/pinterest-ads.png';
import tiktokAdsLogo from '../../../logos/tiktok-ads.png';
import chatgptAdsLogo from '../../../logos/chatgpt-ads.png';
import awinLogo from '../../../logos/awin.png';
import prestashopLogo from '../../../logos/prestashop.png';
import pianoLogo from '../../../logos/piano-analytics.png';
import matomoLogo from '../../../logos/matomo.png';
import searchConsoleLogo from '../../../logos/google-search-console.png';
import tagManagerLogo from '../../../logos/google-tag-manager.png';
import merchantLogo from '../../../logos/google-merchant.png';

const API_URL = config.API_URL;

// Config des providers avec logos
const providerConfig = {
  GOOGLE_ADS: { label: 'Google Ads', logo: googleAdsLogo },
  GOOGLE_SHEETS: { label: 'Google Sheets', logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-drive.png' },
  GOOGLE_MERCHANT_API: { label: 'Google Merchant Center', logo: merchantLogo },
  GOOGLE_ANALYTICS: { label: 'Google Analytics', logo: 'https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png' },
  GOOGLE_SEARCH_CONSOLE: { label: 'Google Search Console', logo: searchConsoleLogo },
  GOOGLE_TAG_MANAGER: { label: 'Google Tag Manager', logo: tagManagerLogo },
  MICROSOFT_ADS: { label: 'Microsoft Ads', logo: microsoftAdsLogo },
  FACEBOOK_ADS: { label: 'Meta Ads', logo: metaAdsLogo },
  PINTEREST_ADS: { label: 'Pinterest Ads', logo: pinterestAdsLogo },
  TIKTOK_ADS: { label: 'TikTok Ads', logo: tiktokAdsLogo },
  MATOMO: { label: 'Matomo', logo: matomoLogo },
  PIANO_ANALYTICS: { label: 'Piano Analytics', logo: pianoLogo },
  OPENAI_ADS: { label: 'ChatGPT Ads', logo: chatgptAdsLogo },
  AWIN: { label: 'Awin', logo: awinLogo },
  PRESTASHOP: { label: 'PrestaShop', logo: prestashopLogo },
  OPEN_METEO: { label: 'Open-Meteo', logo: null }
};

const STEPS = [
  { id: 1, title: 'Connecteur', icon: Plug },
  { id: 2, title: 'Comptes', icon: Package },
  { id: 3, title: 'Site', icon: Globe },
  { id: 4, title: 'Client', icon: Building2 }
];

// Providers où chaque connecteur = un compte (pas de liste de comptes par connecteur)
const MULTI_CONNECTOR_PROVIDERS = ['MATOMO', 'PIANO_ANALYTICS'];

const SetupWizard = ({ isOpen, onClose, connectors, accounts, clients, token, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // État du wizard
  const [selectedConnector, setSelectedConnector] = useState('');
  const [selectedProvider, setSelectedProvider] = useState(''); // Pour Matomo/Piano
  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [siteName, setSiteName] = useState('');
  const [siteDescription, setSiteDescription] = useState('');
  const [selectedClient, setSelectedClient] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [createNewClient, setCreateNewClient] = useState(false);

  // Réinitialiser tout le wizard quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setSelectedConnector('');
      setSelectedProvider('');
      setAvailableAccounts([]);
      setSelectedAccounts([]);
      setSiteName('');
      setSiteDescription('');
      setSelectedClient('');
      setNewClientName('');
      setCreateNewClient(false);
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  // Charger les comptes du connecteur (ou lister les connecteurs pour Matomo/Piano)
  useEffect(() => {
    if (currentStep !== 2) return;

    // Cas spécial: Matomo/Piano - on liste les connecteurs comme comptes
    if (selectedProvider && MULTI_CONNECTOR_PROVIDERS.includes(selectedProvider)) {
      const providerConnectors = connectors
        .filter(c => c.isActive && c.provider === selectedProvider)
        .map(c => ({
          id: c.id,
          sourceId: c.id,
          name: c.connectionName || c.connectorEmail || `${providerConfig[selectedProvider]?.label} - ${c.id.slice(0, 8)}`,
          provider: selectedProvider,
          isConnector: true // Flag pour identifier que c'est un connecteur, pas un compte
        }));
      setAvailableAccounts(providerConnectors);
      return;
    }

    // Cas standard: charger les comptes depuis l'API
    if (!selectedConnector) return;

    const loadAccounts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}/api/connectors/${selectedConnector}/accounts`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const accts = response.data?.accounts || response.data || [];
        // Dédupliquer les comptes (évite les erreurs de clés React)
        const uniqueMap = new Map();
        accts.forEach(a => {
          const id = a.id || a.sourceId || a.customerId;
          if (!uniqueMap.has(id)) {
            uniqueMap.set(id, a);
          }
        });
        const uniqueAccts = Array.from(uniqueMap.values());
        // Filtrer les comptes déjà importés
        const filtered = uniqueAccts.filter(a => {
          const accountId = a.id || a.sourceId || a.customerId;
          return !accounts.some(ea => ea.sourceId === accountId || ea.sourceId === String(accountId));
        });
        setAvailableAccounts(filtered);
      } catch (err) {
        console.error('[WIZARD] Erreur chargement comptes:', err);
        setError('Erreur lors du chargement des comptes');
      } finally {
        setLoading(false);
      }
    };

    loadAccounts();
  }, [selectedConnector, selectedProvider, currentStep, accounts, connectors, token]);

  const handleNext = () => {
    setError(null);
    if (currentStep === 1 && !selectedConnector && !selectedProvider) {
      setError('Sélectionnez un connecteur');
      return;
    }
    if (currentStep === 2 && selectedAccounts.length === 0) {
      setError('Sélectionnez au moins un compte');
      return;
    }
    if (currentStep === 3 && !siteName.trim()) {
      setError('Entrez un nom de site');
      return;
    }
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep <= 1) return;

    // Réinitialiser les données de l'étape AVANT de changer l'étape
    if (currentStep === 2) {
      setAvailableAccounts([]);
      setSelectedAccounts([]);
      setSelectedProvider('');
      setSelectedConnector('');
    }
    setError(null);
    setCurrentStep(currentStep - 1);
  };

  const handleFinish = async () => {
    setLoading(true);
    setError(null);

    try {
      // Déterminer le provider (cas standard vs multi-connecteur)
      const isMultiConnector = selectedProvider && MULTI_CONNECTOR_PROVIDERS.includes(selectedProvider);
      const provider = isMultiConnector ? selectedProvider : connectors.find(c => c.id === selectedConnector)?.provider;

      console.log('[WIZARD] Provider:', provider);
      console.log('[WIZARD] selectedAccounts:', selectedAccounts);
      console.log('[WIZARD] availableAccounts:', availableAccounts.map(a => ({ id: a.id, sourceId: a.sourceId, customerId: a.customerId, propertyId: a.propertyId })));

      // 1. Importer les comptes sélectionnés un par un
      // Convertir tous les IDs en strings pour une comparaison cohérente
      const selectedAccountsStr = selectedAccounts.map(id => String(id));

      const accountsToImport = availableAccounts
        .filter(a => {
          const accountId = String(a.id || a.sourceId || a.customerId || a.propertyId);
          const isSelected = selectedAccountsStr.includes(accountId);
          console.log('[WIZARD] Checking account:', accountId, 'selected:', isSelected);
          return isSelected;
        })
        .map(a => {
          const sourceId = String(a.id || a.sourceId || a.customerId || a.propertyId);
          const baseName = a.name || a.descriptiveName || a.displayName || a.propertyName || 'Compte sans nom';
          // Pour Facebook/Meta Ads, inclure l'ID dans le nom pour faciliter l'identification
          const name = provider === 'FACEBOOK_ADS' ? `${baseName} (${sourceId})` : baseName;
          return {
            sourceId,
            name,
            type: provider,
            connectionId: selectedConnector || a.id // Pour Matomo/Piano, utiliser l'ID du compte comme connectionId
          };
        });

      console.log('[WIZARD] accountsToImport:', accountsToImport);

      /**
       * L'identifiant sous lequel un compte est rattaché à un site.
       *
       * Matomo et Piano numérotent leurs sites **par instance** : « 2 » désigne un site chez l'un
       * et un autre chez le second. Écrire ce numéro nu produit un rattachement qui désigne
       * plusieurs comptes à la fois — les écrans affichent alors le premier venu, souvent celui
       * d'un autre client.
       *
       * Le préfixe de connexion lève l'ambiguïté. Les autres plateformes gardent leur identifiant,
       * unique chez leur fournisseur.
       */
      const identifiantDeRattachement = (compte) => {
        const prefixe = compte.type === 'MATOMO' ? 'matomo'
          : compte.type === 'PIANO_ANALYTICS' ? 'piano'
            : null;
        if (!prefixe || !compte.connectionId) return compte.sourceId;
        return `${prefixe}-${compte.connectionId}-${compte.sourceId}`;
      };

      // Stocker les identifiants des comptes importés avec succès
      const importedSourceIds = [];

      // Ajouter chaque compte à la bibliothèque
      for (const account of accountsToImport) {
        try {
          console.log('[WIZARD] Importing account:', account);

          // Construire les métadonnées selon le type
          const metadata = {};
          if (account.type === 'GOOGLE_ADS') {
            metadata.customerId = String(account.sourceId).replace(/-/g, '');
          } else if (account.type === 'FACEBOOK_ADS') {
            metadata.accountId = String(account.sourceId);
          } else if (account.type === 'MICROSOFT_ADS') {
            metadata.accountId = String(account.sourceId);
          } else if (account.type === 'GOOGLE_ANALYTICS') {
            metadata.propertyId = String(account.sourceId);
          } else if (account.type === 'GOOGLE_SEARCH_CONSOLE') {
            metadata.siteUrl = String(account.sourceId);
          } else if (account.type === 'GOOGLE_TAG_MANAGER') {
            /*
             * ⚠️ **Deux identifiants, et `accountId` n'est pas du confort** : tous les chemins
             * de l'API Tag Manager sont préfixés par `accounts/{accountId}/`. La route des
             * comptes le rend dans la ligne ; le backend le complète s'il manque.
             */
            metadata.containerId = String(account.sourceId);
            if (account.accountId) metadata.accountId = String(account.accountId);
            if (account.publicId) metadata.publicId = account.publicId;
          } else if (account.type === 'MATOMO' || account.type === 'PIANO_ANALYTICS') {
            metadata.siteId = String(account.sourceId);
          }

          await axios.post(
            `${API_URL}/api/library/add`,
            {
              type: account.type,
              // Le nom porte l'identifiant pour les régies : c'est la seule information
              // affichée par les écrans qui listent les comptes.
              name: buildAccountName(account.type, account.name, account.sourceId),
              sourceId: account.sourceId,
              sourceName: account.name,
              connectionId: account.connectionId,
              metadata
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          importedSourceIds.push(identifiantDeRattachement(account));
          console.log('[WIZARD] Account imported successfully:', account.sourceId);
        } catch (importErr) {
          // Si le compte existe déjà (409), l'ajouter quand même aux IDs du site
          if (importErr.response?.status === 409) {
            console.log('[WIZARD] Account already exists, adding to site anyway:', account.sourceId);
            importedSourceIds.push(identifiantDeRattachement(account));
          } else {
            console.error('[WIZARD] Error importing account:', importErr);
            throw importErr;
          }
        }
      }

      // 2. Créer le client si nécessaire
      let clientId = selectedClient || null;

      // Créer si createNewClient est true OU si aucun client n'existe et un nom est saisi
      if ((createNewClient || clients.length === 0) && newClientName.trim()) {
        console.log('[WIZARD] Creating client:', newClientName.trim());
        const clientRes = await axios.post(
          `${API_URL}/api/client-management/clients`,
          { name: newClientName.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        clientId = clientRes.data?.client?.id || clientRes.data?.id;
        console.log('[WIZARD] Client created with ID:', clientId);
      }

      // 3. Créer le site avec les comptes et le client
      console.log('[WIZARD] Creating site with accountIds:', importedSourceIds, 'clientId:', clientId);
      await axios.post(
        `${API_URL}/api/sites`,
        {
          name: siteName.trim(),
          description: siteDescription.trim(),
          accountIds: importedSourceIds,
          clientId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('[WIZARD] Site created successfully');

      onComplete();
      onClose();
    } catch (err) {
      console.error('[WIZARD] Erreur création:', err);
      setError(err.response?.data?.message || err.response?.data?.error || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAccount = (accountId) => {
    setSelectedAccounts(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      }
      return [...prev, accountId];
    });
  };

  if (!isOpen) return null;

  const activeConnectors = connectors.filter(c => c.isActive);

  const modalContentStyle = {
    maxWidth: '560px',
    display: 'flex',
    flexDirection: 'column'
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e5e0d5'
  };

  const titleStyle = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#1e293b'
  };

  const closeButtonStyle = {
    padding: '0.5rem',
    borderRadius: '0.375rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex'
  };

  const stepsContainerStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '1.5rem',
    gap: '0.5rem',
    borderBottom: '1px solid #e5e0d5'
  };

  const stepStyle = (stepNum) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  });

  const stepCircleStyle = (stepNum) => ({
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: stepNum < currentStep ? '#22c55e' : stepNum === currentStep ? '#1e293b' : '#e5e0d5',
    color: stepNum <= currentStep ? 'white' : '#94a3b8',
    fontSize: '0.875rem',
    fontWeight: '600',
    transition: 'all 0.2s'
  });

  const stepLabelStyle = (stepNum) => ({
    fontSize: '0.75rem',
    fontWeight: '500',
    color: stepNum === currentStep ? '#1e293b' : '#94a3b8',
    display: stepNum === currentStep ? 'block' : 'none'
  });

  const stepLineStyle = {
    width: '24px',
    height: '2px',
    backgroundColor: '#e5e0d5'
  };

  const bodyStyle = {
    padding: '1.5rem',
    flex: 1,
    overflowY: 'auto'
  };

  const footerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e5e0d5',
    backgroundColor: '#f3efe7',
    borderRadius: '0 0 0.75rem 0.75rem'
  };

  const buttonStyle = (primary, disabled) => ({
    padding: '0.625rem 1.25rem',
    borderRadius: '0.5rem',
    border: primary ? 'none' : '1px solid #e5e0d5',
    backgroundColor: primary ? (disabled ? '#9ca3af' : '#1e293b') : 'white',
    color: primary ? 'white' : '#64748b',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  });

  const renderStepContent = () => {
    // Séparer les connecteurs multi-connecteur (Matomo/Piano) des autres
    const standardConnectors = activeConnectors.filter(c => !MULTI_CONNECTOR_PROVIDERS.includes(c.provider));
    const multiConnectorProviders = [...new Set(
      activeConnectors
        .filter(c => MULTI_CONNECTOR_PROVIDERS.includes(c.provider))
        .map(c => c.provider)
    )];

    switch (currentStep) {
      case 1:
        return (
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Sélectionnez un connecteur
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
              Choisissez la source de données à utiliser
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {/* Connecteurs standards (Google Ads, Microsoft, etc.) - triés par label */}
              {[...standardConnectors].sort((a, b) => {
                const labelA = providerConfig[a.provider]?.label || a.provider;
                const labelB = providerConfig[b.provider]?.label || b.provider;
                return labelA.localeCompare(labelB, 'fr');
              }).map(connector => {
                const config = providerConfig[connector.provider] || { label: connector.provider, logo: null };
                return (
                  <div
                    key={connector.id}
                    onClick={() => {
                      setSelectedConnector(connector.id);
                      setSelectedProvider('');
                    }}
                    style={{
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      border: selectedConnector === connector.id ? '2px solid #1e293b' : '1px solid #e5e0d5',
                      backgroundColor: selectedConnector === connector.id ? '#f3efe7' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}
                  >
                    {config.logo && (
                      <img
                        src={config.logo}
                        alt={config.label}
                        style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: '#1e293b' }}>
                        {config.label}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.125rem' }}>
                        {connector.connectionName || connector.connectorEmail}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Providers multi-connecteur (Matomo, Piano) - groupés */}
              {multiConnectorProviders.map(providerType => {
                const config = providerConfig[providerType] || { label: providerType, logo: null };
                const count = activeConnectors.filter(c => c.provider === providerType).length;
                return (
                  <div
                    key={providerType}
                    onClick={() => {
                      setSelectedProvider(providerType);
                      setSelectedConnector('');
                    }}
                    style={{
                      padding: '0.875rem 1rem',
                      borderRadius: '0.5rem',
                      border: selectedProvider === providerType ? '2px solid #1e293b' : '1px solid #e5e0d5',
                      backgroundColor: selectedProvider === providerType ? '#f3efe7' : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}
                  >
                    {config.logo && (
                      <img
                        src={config.logo}
                        alt={config.label}
                        style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', color: '#1e293b' }}>
                        {config.label}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.125rem' }}>
                        {count} connecteur{count > 1 ? 's' : ''} configuré{count > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );

      case 2: {
        // Récupérer le label du connecteur sélectionné
        let connectorLabel = '';
        if (selectedProvider && MULTI_CONNECTOR_PROVIDERS.includes(selectedProvider)) {
          connectorLabel = providerConfig[selectedProvider]?.label || selectedProvider;
        } else if (selectedConnector) {
          const connector = connectors.find(c => c.id === selectedConnector);
          connectorLabel = providerConfig[connector?.provider]?.label || connector?.provider || '';
        }

        return (
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Sélectionnez le(s) compte(s) {connectorLabel}
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
              Cochez les comptes à regrouper dans le site
            </p>
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
                <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#64748b' }} />
              </div>
            ) : availableAccounts.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                Aucun nouveau compte disponible
              </div>
            ) : (
              <div style={{ border: '1px solid #e5e0d5', borderRadius: '0.5rem', maxHeight: '250px', overflowY: 'auto' }}>
                {availableAccounts.map(account => {
                  const accountId = account.id || account.sourceId || account.customerId;
                  const accountName = account.name || account.descriptiveName || account.displayName || 'Compte sans nom';
                  const isSelected = selectedAccounts.includes(accountId);
                  return (
                    <div
                      key={accountId}
                      onClick={() => handleToggleAccount(accountId)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.75rem 1rem',
                        borderBottom: '1px solid #f3efe7',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#f3efe7' : 'white'
                      }}
                    >
                      <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '0.25rem',
                        border: isSelected ? 'none' : '2px solid #cbd5e1',
                        backgroundColor: isSelected ? '#1e293b' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {isSelected && <Check size={12} color="white" />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: '#1e293b' }}>
                          {accountName}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', fontFamily: 'monospace' }}>
                          {accountId}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      case 3:
        return (
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Créez le site
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
              Donnez un nom à ce regroupement de comptes
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '0.5rem' }}>
                Nom du site *
              </label>
              <input
                type="text"
                value={siteName}
                onChange={(e) => setSiteName(e.target.value)}
                placeholder="Ex: Site E-commerce Principal"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e0d5',
                  fontSize: '0.875rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '0.5rem' }}>
                Description (optionnel)
              </label>
              <textarea
                value={siteDescription}
                onChange={(e) => setSiteDescription(e.target.value)}
                placeholder="Décrivez ce site..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e0d5',
                  fontSize: '0.875rem',
                  outline: 'none',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          </div>
        );

      case 4:
        // Si aucun client n'existe, proposer uniquement la création
        if (clients.length === 0) {
          return (
            <div>
              <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                Créer un client (optionnel)
              </h3>
              <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
                Vous pouvez créer un client pour organiser vos sites
              </p>

              <input
                type="text"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                placeholder="Nom du client"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e0d5',
                  fontSize: '0.875rem',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
          );
        }

        return (
          <div>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
              Assignez à un client (optionnel)
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
              Vous pouvez lier ce site à un client existant ou en créer un nouveau
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
                <input
                  type="radio"
                  checked={!createNewClient}
                  onChange={() => setCreateNewClient(false)}
                />
                <span style={{ fontSize: '0.875rem' }}>Client existant</span>
              </label>

              {!createNewClient && (
                <select
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e0d5',
                    fontSize: '0.875rem',
                    outline: 'none',
                    marginBottom: '1rem'
                  }}
                >
                  <option value="">Aucun client (laisser non assigné)</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.75rem' }}>
                <input
                  type="radio"
                  checked={createNewClient}
                  onChange={() => setCreateNewClient(true)}
                />
                <span style={{ fontSize: '0.875rem' }}>Créer un nouveau client</span>
              </label>

              {createNewClient && (
                <input
                  type="text"
                  value={newClientName}
                  onChange={(e) => setNewClientName(e.target.value)}
                  placeholder="Nom du client"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e0d5',
                    fontSize: '0.875rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999
    }}>
      <div style={{
        ...modalContentStyle,
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        width: '90vw',
        maxHeight: '90vh'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={titleStyle}>Assistant de configuration</div>
          <button style={closeButtonStyle} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={stepsContainerStyle}>
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <div style={stepStyle(step.id)}>
                <div style={stepCircleStyle(step.id)}>
                  {step.id < currentStep ? <Check size={16} /> : step.id}
                </div>
                <span style={stepLabelStyle(step.id)}>{step.title}</span>
              </div>
              {index < STEPS.length - 1 && <div style={stepLineStyle} />}
            </React.Fragment>
          ))}
        </div>

        <div style={bodyStyle} key={`step-${currentStep}`}>
          {renderStepContent()}

          {error && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fef2f2', borderRadius: '0.5rem', color: '#dc2626', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}
        </div>

        <div style={footerStyle}>
          {currentStep > 1 ? (
            <button
              style={buttonStyle(false, false)}
              onClick={handlePrevious}
            >
              <ChevronLeft size={16} />
              Précédent
            </button>
          ) : (
            <div />
          )}

          {currentStep < 4 ? (
            <button style={buttonStyle(true, false)} onClick={handleNext}>
              Suivant
              <ChevronRight size={16} />
            </button>
          ) : (
            <button
              style={buttonStyle(true, loading)}
              onClick={handleFinish}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Création...
                </>
              ) : (
                <>
                  <Check size={16} />
                  Terminer
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SetupWizard;
